import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import {
  MultipleChoiceAdminPageData,
  MultipleChoiceAdminPageDataVariables,
  MultipleChoiceQuestionFragment,
  ReorderMultipleChoiceQuestion,
  ReorderMultipleChoiceQuestionVariables,
} from "__generated__/types";
import { Button } from "common/buttons";
import { Box } from "common/components/Box";
import { NoDataError } from "common/components/NoDataError";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { AdministrationLayout } from "common/components/layouts/AdministrationLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { ListContainer } from "common/components/lists/ListContainer";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { useToggle } from "common/hooks/useToggle";
import { adminMultipleChoiceFragment } from "domain/admin/fragments";
import { contentBreadcrumbFragment } from "domain/pages/breadcrumbs";
import update from "immutability-helper";
import * as React from "react";
import { useCallback } from "react";
import { useParams } from "react-router";
import { CreateMultipleChoiceQuestionForm } from "../multiple-choice/MultipleChoiceQuestionCreateForm";
import { QuestionListItem } from "../multiple-choice/QuestionListItem";

const MUTATION = gql`
  mutation ReorderMultipleChoiceQuestion(
    $input: ReorderMultipleChoiceQuestionInput!
  ) {
    reorderMultipleChoiceQuestion(input: $input) {
      question {
        id
      }
    }
  }
`;

const GET_MULTIPLE_CHOICE = gql`
  query MultipleChoiceAdminPageData($multipleChoiceId: ID!) {
    multipleChoiceById(id: $multipleChoiceId) {
      ...ContentBreadcrumbFragment
      ...AdminMultipleChoiceFragment
    }
  }
  ${contentBreadcrumbFragment}
  ${adminMultipleChoiceFragment}
`;

type RouteProps = {
  multipleChoiceId: string;
};

export const MultipleChoice: React.FC = () => {
  const { multipleChoiceId } = useParams<RouteProps>();

  const [createQuestion, setCreateQuestion] = useToggle(false);
  const [draggableElements, setDraggableElements] = React.useState<
    MultipleChoiceQuestionFragment[]
  >([]);

  const { data, loadingOrError, refetch } = useSimpleQuery<
    MultipleChoiceAdminPageData,
    MultipleChoiceAdminPageDataVariables
  >(GET_MULTIPLE_CHOICE, {
    variables: {
      multipleChoiceId: multipleChoiceId as string,
    },
  });

  React.useEffect(() => {
    if (data?.multipleChoiceById?.questions) {
      setDraggableElements(data.multipleChoiceById.questions);
    }
  }, [data]);

  const [mutate, { error, loading }] = useMutation<
    ReorderMultipleChoiceQuestion,
    ReorderMultipleChoiceQuestionVariables
  >(MUTATION);

  const moveElement = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setDraggableElements((prevElements) =>
        update(prevElements, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, prevElements[dragIndex]],
          ],
        }),
      );
    },
    [draggableElements],
  );

  const saveOrder = useCallback(
    async (dragIndex: number, dropIndex: number, id: string) => {
      const moveToAfterElement =
        dropIndex === 0 ? null : (draggableElements[dropIndex - 1] ?? null);
      await mutate({
        variables: {
          input: {
            multipleChoiceId: multipleChoiceId as string,
            questionId: id,
            moveToAfterQuestionId: moveToAfterElement?.id,
          },
        },
        refetchQueries: [
          {
            query: GET_MULTIPLE_CHOICE,
            variables: { multipleChoiceId: multipleChoiceId as string },
          },
        ],
      });
    },
    [draggableElements],
  );

  if (!multipleChoiceId) {
    return <div>Multiple Choice ID is not provided or invalid.</div>;
  }

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.multipleChoiceById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const { questions, title, context, type } = data.multipleChoiceById;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
  const isStandard = type !== "GROUPING";

  return (
    <AdministrationLayout title={title} context={context ?? []}>
      <MutationLoadingOrError loading={loading} error={error} />
      {questions.length === 0 ? (
        <Box centeredContent>
          <p>Oppgaven har ikke noen spørsmål enda</p>
          <Button onClick={setCreateQuestion}>
            {isStandard ? "Legg til spørsmål" : "Legg til kategori"}
          </Button>
        </Box>
      ) : (
        <>
          <ListContainer>
            {draggableElements.map((question, index) => (
              <QuestionListItem
                moveElement={moveElement}
                saveOrder={saveOrder}
                key={question.id}
                index={index}
                question={question}
                multipleChoiceId={multipleChoiceId}
                type={type}
                refetch={refetch}
              />
            ))}
          </ListContainer>
          {/* TODO: Replace with + button. */}
          {!createQuestion && (
            <Box centeredContent>
              <Button onClick={setCreateQuestion}>
                {isStandard ? "Legg til spørsmål" : "Legg til kategori"}
              </Button>
            </Box>
          )}
        </>
      )}
      {createQuestion && (
        <CreateMultipleChoiceQuestionForm
          multipleChoiceId={multipleChoiceId}
          type={type}
          cancel={() => {
            setCreateQuestion();
          }}
          complete={async () => {
            setCreateQuestion();
            await refetch();
          }}
        />
      )}
    </AdministrationLayout>
  );
};
